import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createVisibleToColumn } from "./VisibleToCell";
import {
  Button,
  Table,
  Select,
  Tag,
  Popover,
  Input,
  Modal,
  message,
  Tooltip,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  MoreOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  ShareAltOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Container } from "./style";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { setChallengeData } from "../../../store/features/CreateChallenge";
import EnrolledUsersModal from "./EnrolledUsersModal";
import ActiveUsersModal from "./ActiveUsersModal";
const { confirm } = Modal;

const LIST_CHALLENGES = gql`
  query ListChallenges(
    $filters: ListChallengeInput
    $args: PaginationArgs
    $sortBy: SortByArgs
  ) {
    listChallenges(filters: $filters, args: $args, sortBy: $sortBy) {
      count
      limit
      offset
      total
      challenges {
        _id
        aiGenerationPrompt
        banner
        coach {
          _id
          name
          email
          belief
          about
          experience
        }
        completesCount
        createdAt
        createdBy {
          _id
          name
          email
          belief
          about
          experience
        }
        days {
          _id
          action {
            _id
            content
            completesCount
            viewsCount
            uniqueVisitorsCount
          }
          dayNumber
          insight {
            _id
            content
            link
            completesCount
            viewsCount
            linkClicksCount
            linkPreview {
              url
              status
              provider
              playbackMode
              title
              description
              imageUrl
              siteName
              faviconUrl
              domain
            }
          }
          prompt {
            _id
            content
            completesCount
            viewsCount
            repliesCount
            uniqueVisitorsCount
          }
          order
        }
        deletedAt
        description
        enrolledCount
        group {
          name
          memberCount
        }
        activeCount
        isAIGenerated
        numberOfDays
        publishedAt
        scheduledAt
        status
        title
        updatedAt
        uploadedFileUrl
        viewsCount
        visibility
        visibilityType
        visibilityGroupId
      }
    }
  }
`;

const UPDATE_CHALLENGE = gql`
  mutation updateChallenge($updateChallengeInput: UpdateChallengeInput!) {
    updateChallenge(payload: $updateChallengeInput) {
      _id
    }
  }
`;

const DELETE_CHALLENGE = gql`
  mutation deleteChallenge($_id: String!) {
    deleteChallenge(_id: $_id) {
      _id
    }
  }
`;

const isChallengeSessionActive = (scheduledAt, numberOfDays, status) => {
  if (status === "DRAFTED" || status === "HIDDEN") return false;
  if (!scheduledAt || !numberOfDays) return false;
  const start = dayjs(scheduledAt);
  const end = start.add(numberOfDays - 1, "day").endOf("day");
  const now = dayjs();
  return !now.isBefore(start) && !now.isAfter(end);
};

const formatSessionDateRange = (scheduledAt, numberOfDays) => {
  const start = dayjs(scheduledAt);
  const end = start.add((numberOfDays || 1) - 1, "day");
  return `${start.format("M/D")} ~ ${end.format("M/D, YYYY")}`;
};

const DAY_CONTENT_CONFIG = {
  reflection: { icon: "📝", label: "Reflection", variant: "reflection" },
  insight: { icon: "💡", label: "Insight", variant: "insight" },
  action: { icon: "🎯", label: "Action", variant: "action" },
};

function DayContentStat({ label, value }) {
  return (
    <span className="challenge-day-stat">
      <span className="challenge-day-stat-label">{label}</span>
      <span className="challenge-day-stat-value">{value ?? 0}</span>
    </span>
  );
}

function DayContentCard({ type, content, stats = [], link }) {
  const config = DAY_CONTENT_CONFIG[type];
  const visibleStats = stats.filter(({ show }) => show);

  return (
    <div className={`challenge-day-card challenge-day-card--${config.variant}`}>
      <div className="challenge-day-card-header">
        <div className="challenge-day-card-title">
          <span className="challenge-day-card-icon">{config.icon}</span>
          {config.label}
        </div>
        {visibleStats.length > 0 && (
          <div className="challenge-day-card-stats">
            {visibleStats.map(({ label, value }) => (
              <DayContentStat key={label} label={label} value={value} />
            ))}
          </div>
        )}
      </div>
      <div className="challenge-day-card-content">{content}</div>
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="challenge-day-card-link"
        >
          🔗 Learn more
        </a>
      )}
    </div>
  );
}

export default function ChallengeManager({ onDeleteSuccess }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState({});
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [enrolledModalVisible, setEnrolledModalVisible] = useState(false);
  const [activeModalVisible, setActiveModalVisible] = useState(false);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [weekStartDate] = useState(() => {
    // 计算当前周的起始日期（周一）
    const today = dayjs();
    const startOfWeek = today.startOf("week").add(1, "day"); // dayjs默认周日为一周开始，加1天得到周一
    return startOfWeek.toISOString();
  });
  const initVariables = {
    filters: {
      createdBy: userInfo.coach?._id || null,
      status: null,
    },
    args: {
      limit: 10,
      offset: 0,
    },
    sortBy: {
      createdAt: -1,
      publishedAt: -1,
      scheduledAt: -1,
    },
  };

  // eslint-disable-next-line no-unused-vars
  const [getChallenges, { data, refetch }] = useLazyQuery(LIST_CHALLENGES, {
    variables: initVariables,
  });
  const [updateChallenge] = useMutation(UPDATE_CHALLENGE);
  const [deleteChallenge] = useMutation(DELETE_CHALLENGE);

  const statusList = [
    {
      label: "Status: All",
      value: "all",
    },
    {
      label: "Status: Published",
      value: "PUBLISHED",
    },
    {
      label: "Status: Draft",
      value: "DRAFTED",
    },
    {
      label: "Status: Scheduled",
      value: "SCHEDULED",
    },
    {
      label: "Status: Hide",
      value: "HIDDEN",
    },
  ];

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title, row) => {
        const isActive = isChallengeSessionActive(
          row.scheduledAt,
          row.numberOfDays,
          row.status
        );

        return (
          <span className="challenge-title-cell">
            {isActive && (
              <Tooltip title="Live now">
                <span className="challenge-active-badge">
                  <ThunderboltOutlined />
                </span>
              </Tooltip>
            )}
            <span className={isActive ? "challenge-active-title" : ""}>
              {title}
            </span>
          </span>
        );
      },
    },
    {
      title: (
        <span>
          Session{" "}
          <Tag
            style={{
              background: "#fff7e6",
              color: "#d48806",
              border: "none",
              fontWeight: 500,
            }}
          >
            new
          </Tag>
        </span>
      ),
      key: "session",
      render: (_, row) => {
        if (!row.scheduledAt) return "-";
        const days = row.numberOfDays || 0;
        const dayLabel = days === 1 ? "Day" : "Days";

        return (
          <div>
            <div style={{ fontWeight: 600, color: "#262626" }}>
              {formatSessionDateRange(row.scheduledAt, days)}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 4,
                color: "#595959",
                fontSize: 13,
              }}
            >
              <ClockCircleOutlined />
              <span>{dayjs(row.scheduledAt).format("h:mm A")}</span>
              <span>·</span>
              <Tag
                style={{
                  background: "#f6ffed",
                  color: "#389e0d",
                  border: "none",
                  borderRadius: 12,
                  margin: 0,
                }}
              >
                {days} {dayLabel}
              </Tag>
            </div>
          </div>
        );
      },
    },
    createVisibleToColumn({ navigate }),
    {
      title: "Enrolled",
      dataIndex: "enrolledCount",
      render: (enrolledCount, row) => (
        <Tooltip title="Click to view enrolled users">
          <span
            className="c-blue pointer"
            onClick={() => handleShowEnrolledList(row)}
          >
            {enrolledCount || 0}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Active",
      dataIndex: "activeCount",
      render: (activeCount, row) => (
        <Tooltip title="Click to view active users">
          <span
            className="c-blue pointer"
            onClick={() => handleShowActiveList(row)}
          >
            {activeCount || 0}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Share",
      dataIndex: "share",
      render: (share, row) => {
        if (row.status === "PUBLISHED") {
          return (
            <Tooltip title="Copy Link">
              <Button
                type="primary"
                shape="circle"
                icon={<ShareAltOutlined />}
                onClick={() => onCopyLink(row)}
              />
            </Tooltip>
          );
        }
        return "-";
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 80,
      render: (action, row) => {
        return (
          <Popover
            placement="right"
            content={
              <ul>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onEdit(row)}
                  >
                    Edit
                  </Button>
                </li>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onDuplicate(row)}
                  >
                    Duplicate
                  </Button>
                </li>
                <li>
                  {row.status !== "HIDDEN" && (
                    <Button
                      type="text"
                      block
                      className="text-left"
                      onClick={() => onHide(row)}
                    >
                      Hide
                    </Button>
                  )}
                </li>
                <li>
                  {row.status === "HIDDEN" && (
                    <Button
                      type="text"
                      block
                      className="text-left"
                      onClick={() => onUnhide(row)}
                    >
                      Unhide
                    </Button>
                  )}
                </li>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onDelete(row)}
                  >
                    Delete
                  </Button>
                </li>
              </ul>
            }
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: "16px" }} />}
            ></Button>
          </Popover>
        );
      },
    },
  ];

  const onCopyLink = (row) => {
    if (!row?._id) {
      message.error("Challenge ID is missing");
      return;
    }
    // Build the share link URL
    const shareUrl = `${window.location.origin}/ShareChallenge?challengeId=${row._id}`;
    // Copy to clipboard
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        message.success("Link copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        message.error("Failed to copy link");
      });
  };
  useEffect(() => {
    setTableParams(initVariables);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  useEffect(() => {
    if (data?.listChallenges?.challenges) {
      setList(data.listChallenges.challenges);
    } else {
      setList([]);
    }
    setTotal(data?.listChallenges?.total || 0);
  }, [data]);

  const getData = async () => {
    if (!tableParams.args || !tableParams.args.limit) {
      return;
    }
    setLoading(true);
    await refetch(tableParams);
    setLoading(false);
  };
  const onSearch = (val) => {
    let param = {
      filters: Object.assign({}, tableParams.filters, {
        title: val.target.value,
      }),
      args: tableParams.args,
    };
    if (!val.target.value) {
      delete param.filters.title;
    }
    setTableParams(param);
  };
  const onStatusChange = (val) => {
    let param = { filters: tableParams.filters, args: tableParams.args };
    if (val === "all") {
      param.filters.status = null;
    } else if (val) {
      param.filters.status = val;
    }
    setTableParams(param);
  };

  const onTableChange = (pagination, filters, sorter) => {
    console.log(pagination);
    setTableParams({
      filters: tableParams.filters,
      args: {
        limit: pagination.pageSize,
        offset: pagination.pageSize * (pagination.current - 1),
      },
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.args?.limit) {
      setList([]);
    }
  };

  const handleShowEnrolledList = (challenge) => {
    setCurrentChallenge(challenge);
    setEnrolledModalVisible(true);
  };

  const handleShowActiveList = (challenge) => {
    setCurrentChallenge(challenge);
    setActiveModalVisible(true);
  };

  const handleCloseEnrolledModal = () => {
    setEnrolledModalVisible(false);
    setCurrentChallenge(null);
  };

  const handleCloseActiveModal = () => {
    setActiveModalVisible(false);
    setCurrentChallenge(null);
  };

  // Actions
  const onEdit = (data) => {
    dispatch(setChallengeData(data));
    navigate("/EditChallenge");
  };
  const onHide = (data) => {
    confirm({
      title: `Hide the challenge "${data.title}"?`,
      content: "Are you sure to hide this challenge",
      okText: "Yes, hide this challenge",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          await updateChallenge({
            variables: {
              updateChallengeInput: {
                _id: data._id,
                status: "HIDDEN",
              },
            },
          });
          message.success(`Challenge "${data.title}" is hidden from the app!`);
          getData();
        } catch (error) {
          message.error("Failed to hide challenge");
        }
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onUnhide = (data) => {
    confirm({
      title: `Unhide the challenge "${data.title}"?`,
      content: "Are you sure to unhide this challenge",
      okText: "Yes, unhide this challenge",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          await updateChallenge({
            variables: {
              updateChallengeInput: {
                _id: data._id,
                status: "PUBLISHED",
              },
            },
          });
          message.success(
            `Challenge "${data.title}" is unhidden and available in the app!`
          );
          getData();
        } catch (error) {
          message.error("Failed to unhide challenge");
        }
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onDelete = (data) => {
    confirm({
      title: `Delete the challenge "${data.title}"?`,
      content:
        "Deleting this challenge will permanently remove it from your account. Are you sure to delete this challenge?",
      okText: "Yes, delete this challenge",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          await deleteChallenge({
            variables: {
              _id: data._id,
            },
          });
          message.success(`Challenge "${data.title}" is deleted successfully!`);
          getData();
          onDeleteSuccess && onDeleteSuccess();
        } catch (error) {
          message.error("Failed to delete challenge");
        }
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onDuplicate = (data) => {
    dispatch(setChallengeData({ ...data, status: "DRAFTED" }));
    navigate("/DuplicateChallenge");
  };
  return (
    <Container>
      <div className="flex-between">
        <Input
          size="large"
          placeholder="Search"
          bordered={false}
          prefix={
            <SearchOutlined style={{ fontSize: "20px", marginRight: "10px" }} />
          }
          className="table-search"
          onPressEnter={onSearch}
        />
        <div className="table-filter">
          <Select
            defaultValue="Status: All"
            style={{ width: 150 }}
            onChange={onStatusChange}
            options={statusList}
          />
        </div>
      </div>
      <Table
        columns={columns}
        rowKey={(record) => record._id}
        dataSource={list}
        pagination={{ total }}
        loading={loading}
        onChange={onTableChange}
        rowClassName={(record) =>
          isChallengeSessionActive(
            record.scheduledAt,
            record.numberOfDays,
            record.status
          )
            ? "challenge-session-active"
            : ""
        }
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ padding: "16px 0" }}>
              {/* Daily Contents */}
              {record.days && record.days.length > 0 && (
                <div>
                  <h4 className="challenge-daily-contents-title">
                    Daily Contents
                  </h4>
                  {record.days.map((day, index) => (
                    <div
                      key={day._id || index}
                      className="challenge-day-block"
                    >
                      <h5 className="challenge-day-block-title">
                        <span className="challenge-day-block-number">
                          Day {day.dayNumber || index + 1}
                        </span>
                        {record.scheduledAt && (
                          <span className="challenge-day-block-date">
                            {dayjs(record.scheduledAt)
                              .add(day.dayNumber - 1, "day")
                              .format("YYYY-MM-DD")}
                          </span>
                        )}
                      </h5>

                      <div className="challenge-day-cards">
                        {day.prompt?.content && (
                          <DayContentCard
                            type="reflection"
                            content={day.prompt.content}
                            stats={[
                              {
                                label: "Replies",
                                value: day.prompt.repliesCount,
                                show:
                                  day.prompt.repliesCount !== undefined,
                              },
                              {
                                label: "Views",
                                value: day.prompt.viewsCount,
                                show: day.prompt.viewsCount !== undefined,
                              },
                              {
                                label: "Unique visitors",
                                value: day.prompt.uniqueVisitorsCount,
                                show:
                                  day.prompt.uniqueVisitorsCount !== undefined,
                              },
                            ]}
                          />
                        )}

                        {day.insight?.content && (
                          <DayContentCard
                            type="insight"
                            content={day.insight.content}
                            link={day.insight.link}
                            stats={[
                              {
                                label: "Completes",
                                value: day.insight.completesCount,
                                show:
                                  day.insight.completesCount !== undefined,
                              },
                              {
                                label: "Link clicks",
                                value: day.insight.linkClicksCount,
                                show:
                                  day.insight.linkClicksCount !== undefined,
                              },
                            ]}
                          />
                        )}

                        {day.action?.content && (
                          <DayContentCard
                            type="action"
                            content={day.action.content}
                            stats={[
                              {
                                label: "Completes",
                                value: day.action.completesCount,
                                show:
                                  day.action.completesCount !== undefined,
                              },
                              {
                                label: "Views",
                                value: day.action.viewsCount,
                                show: day.action.viewsCount !== undefined,
                              },
                              {
                                label: "Unique visitors",
                                value: day.action.uniqueVisitorsCount,
                                show:
                                  day.action.uniqueVisitorsCount !== undefined,
                              },
                            ]}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ),
          rowExpandable: (record) => record.days && record.days.length > 0,
        }}
      />
      <EnrolledUsersModal
        open={enrolledModalVisible}
        challenge={currentChallenge}
        onCancel={handleCloseEnrolledModal}
      />
      <ActiveUsersModal
        open={activeModalVisible}
        challenge={currentChallenge}
        weekStartDate={weekStartDate}
        onCancel={handleCloseActiveModal}
      />
    </Container>
  );
}